<script setup lang="ts">
import { ref, onMounted, inject, Ref, computed } from 'vue'
import { useMedia } from '@/services/Media'
import { Member } from '@/types/Member'
import { Media, defaultMedia } from '@/types/Media'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import MediaThumb from '@/components/MediaThumb.vue'
import MediaList from '@/components/MediaList.vue'
import permissions from '@/services/permissions'
import { ShieldCheck, Clock, AlertTriangle, Flag, CheckCircle2 as CheckCircleIcon, XCircle as XCircleIcon, Trash2 as TrashIcon } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

const { setModerationStatus, trashFile } = useMedia()
const { checkPermission } = permissions()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const selectedTab = ref('pending')
const selectedMedia = ref<Media[]>([])
const refreshKey = ref(0)
const showDenyDialog = ref(false)
const denyNote = ref('')
const denyingMediaId = ref<number | null>(null)
const pendingCount = ref(0)
const approvedTodayCount = ref(0)

const moderationFilters = computed(() => {
	if (selectedTab.value === 'pending') {
		return { moderationStatus: '0' }
	}

	if (selectedTab.value === 'approved') {
		return { moderationStatus: '1' }
	}

	if (selectedTab.value === 'denied') {
		return { moderationStatus: '2' }
	}

	if (selectedTab.value === 'trashed') {
		return { moderationStatus: '100' }
	}

	return { moderationStatus: '-1' }
})

const handleApprove = async (mediaId: number) => {
	try {
		await setModerationStatus(mediaId, 1)
		toast('Approved', { description: 'Media has been approved.' })
		refreshKey.value++
	} catch (err) {
		toast('Error', { description: 'Failed to approve media.' })
	}
}

const handleBulkApprove = async () => {
	for (const media of selectedMedia.value) {
		await setModerationStatus(media.id, 1)
	}
	toast('Bulk Approved', { description: `${selectedMedia.value.length} items approved.` })
	selectedMedia.value = []
	refreshKey.value++
}

const openDenyDialog = (mediaId: number) => {
	denyingMediaId.value = mediaId
	denyNote.value = ''
	showDenyDialog.value = true
}

const handleDeny = async () => {
	if (denyingMediaId.value === null) {
		return
	}

	try {
		await setModerationStatus(denyingMediaId.value, 2, denyNote.value || undefined)
		toast('Denied', { description: 'Media has been denied.' })
		showDenyDialog.value = false
		refreshKey.value++
	} catch (err) {
		toast('Error', { description: 'Failed to deny media.' })
	}
}

const handleBulkDeny = async () => {
	for (const media of selectedMedia.value) {
		await setModerationStatus(media.id, 2)
	}
	toast('Bulk Denied', { description: `${selectedMedia.value.length} items denied.` })
	selectedMedia.value = []
	refreshKey.value++
}

const handleTrash = async (mediaId: number) => {
	try {
		await trashFile(mediaId)
		toast('Trashed', { description: 'Media has been moved to trash.' })
		refreshKey.value++
	} catch (err) {
		toast('Error', { description: 'Failed to trash media.' })
	}
}

const setTotalCount = (count: number) => {
	if (selectedTab.value === 'pending') {
		pendingCount.value = count
	}
}
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="flex justify-end items-center" v-if="selectedMedia.length > 0">
			<div class="flex space-x-2">
				<Button @click="handleBulkApprove">
					<CheckCircleIcon class="h-4 w-4 mr-2" />
					Approve ({{ selectedMedia.length }})
				</Button>
				<Button variant="destructive" @click="handleBulkDeny">
					<XCircleIcon class="h-4 w-4 mr-2" />
					Deny ({{ selectedMedia.length }})
				</Button>
			</div>
		</div>

		<div class="grid gap-4 md:grid-cols-4">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Pending Review</CardTitle>
					<Clock class="h-5 w-5 text-yellow-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ pendingCount }}</div>
					<p class="text-xs text-muted-foreground">Awaiting moderation</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Approved</CardTitle>
					<ShieldCheck class="h-5 w-5 text-green-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ approvedTodayCount }}</div>
					<p class="text-xs text-muted-foreground">Approved today</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Denied</CardTitle>
					<AlertTriangle class="h-5 w-5 text-red-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">-</div>
					<p class="text-xs text-muted-foreground">Denied today</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Flagged</CardTitle>
					<Flag class="h-5 w-5 text-orange-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">-</div>
					<p class="text-xs text-muted-foreground">Items requiring attention</p>
				</CardContent>
			</Card>
		</div>

		<Card>
			<CardContent class="pt-6">
				<Tabs v-model="selectedTab" class="space-y-4">
					<TabsList>
						<TabsTrigger value="pending">Pending</TabsTrigger>
						<TabsTrigger value="approved">Approved</TabsTrigger>
						<TabsTrigger value="denied">Denied</TabsTrigger>
						<TabsTrigger value="trashed">Trashed</TabsTrigger>
					</TabsList>

					<div class="mt-4">
						<MediaList
							v-if="vhostId !== null"
							v-model="selectedMedia"
							:filters="moderationFilters"
							type="grid"
							:vhostId="vhostId"
							sort-order="upload DESC"
							default-profile="11"
							:refreshKey="refreshKey"
							@set-total-count="setTotalCount"
						/>
					</div>
				</Tabs>
			</CardContent>
		</Card>

		<Dialog v-model:open="showDenyDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Deny Media</DialogTitle>
					<DialogDescription>Provide a reason for denying this content (optional)</DialogDescription>
				</DialogHeader>
				<div class="space-y-4 py-4">
					<Textarea v-model="denyNote" rows="3" placeholder="Reason for denial..." />
				</div>
				<DialogFooter>
					<Button variant="outline" @click="showDenyDialog = false">Cancel</Button>
					<Button variant="destructive" @click="handleDeny">Deny</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>
